import { useEffect, useState } from "react";
import { Instagram, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE, SUPPORT_EMAIL } from "@/lib/content";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full border border-line bg-transparent px-4 py-3.5 font-mono text-sm text-bone placeholder:text-bone/30 outline-none transition-colors focus:border-violet";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "CONTACT — REQUIEM FEVER";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("PLEASE FILL IN EVERY FIELD");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("MESSAGE SENT — WE USUALLY REPLY WITHIN A DAY");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("COULD NOT SEND — PLEASE DM US ON INSTAGRAM");
    }
    setSending(false);
  };

  return (
    <div className="bg-ink" data-testid="contact-page">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        <FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">GET IN TOUCH</p>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h1 className="mt-4 font-display text-5xl leading-[0.9] tracking-[0.01em] text-bone sm:text-7xl">
            TALK TO US.
          </h1>
        </FadeUp>

        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                data-testid="contact-email-card"
                className="group flex items-start gap-4 border border-line p-6 transition-colors hover:border-violet"
              >
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">SUPPORT</p>
                  <p className="mt-2 font-mono text-sm text-bone transition-colors group-hover:text-gold">{SUPPORT_EMAIL}</p>
                </div>
              </a>
            </FadeUp>
            <FadeUp delay={0.08}>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-card"
                className="group mt-4 flex items-start gap-4 border border-line p-6 transition-colors hover:border-violet"
              >
                <Instagram className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">FASTEST REPLY</p>
                  <p className="mt-2 font-mono text-sm text-bone transition-colors group-hover:text-gold">
                    {INSTAGRAM_HANDLE} — DM for sizing, orders & everything else
                  </p>
                </div>
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <input
                data-testid="contact-name-input"
                aria-label="Your name"
                placeholder="NAME"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                aria-label="Your email"
                type="email"
                placeholder="EMAIL"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
              <textarea
                data-testid="contact-message-input"
                aria-label="Your message"
                placeholder="MESSAGE"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-send-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-3 bg-violet px-10 py-4 font-display text-xl tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-magenta disabled:opacity-50"
              >
                {sending ? "SENDING…" : "SEND MESSAGE"}
                <Send className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
